import { ProcessorEntry } from './types';
import { CafInboxPanel } from './panels/CafInboxPanel';
import { CafBacklogPanel } from './panels/CafBacklogPanel';
import { PostBacklogPanel } from './panels/PostBacklogPanel';
import { PhotoIdPanel } from './panels/PhotoIdPanel';

export const processorRegistry: ProcessorEntry[] = [
  {
    id: 'caf_inbox',
    name: 'CAF Inbox',
    description: 'Fetches and files new CAF inbox emails into tasks',
    hasStatePanel: true,
    component: CafInboxPanel,
  },
  {
    id: 'caf_backlog',
    name: 'CAF Backlog',
    description: 'AI classification of the CAF email backlog',
    hasStatePanel: true,
    component: CafBacklogPanel,
  },
  {
    id: 'post_backlog',
    name: 'Post Backlog',
    description: 'Reclassifies existing post tasks using improved AI classification. Processes both completed and pending tasks, flags category changes, and triggers automations for eligible pending tasks.',
    hasStatePanel: true,
    component: PostBacklogPanel,
  },

  // --- Task 2: simple processors ported from ProcessorsManagement.tsx ------

  {
    id: 'email_cancellation',
    name: 'Email Cancellation',
    description: 'Automatically processes cancellation emails from the inbox that are N+ days old.',
    statusEndpoint: '/ui/admin/cancellation-processor',
    enableEndpoint: '/ui/admin/cancellation-processor/enable',
    disableEndpoint: '/ui/admin/cancellation-processor/disable',
    runActions: [
      { label: 'Run Once Now', endpoint: '/ui/admin/cancellation-processor/run', variant: 'primary' },
      {
        label: 'Update Config', endpoint: '/ui/admin/cancellation-processor/config', variant: 'secondary',
        body: (cfg) => ({ minEmailAgeDays: Number(cfg.minEmailAgeDays) }),
      },
    ],
    configInputs: [
      { key: 'minEmailAgeDays', label: 'Process emails older than (days)', type: 'number', defaultValue: '3' },
    ],
    hasStatePanel: true,
  },

  {
    id: 'dsar_inbox',
    name: 'DSAR Inbox',
    description: 'Processes incoming DSAR emails from dsar@complexlaw.co.uk to create tasks for data subject access requests.',
    statusEndpoint: '/ui/admin/dsar-inbox-processor',
    disableEndpoint: '/ui/admin/dsar-inbox-processor/disable',
    runActions: [
      // The old select-a-mode-then-Enable flow has no select input in the schema;
      // each mode is exposed as its own Enable action with the same body the old
      // handler sent (`{ mode }`).
      { label: 'Enable (Single Email Mode)', endpoint: '/ui/admin/dsar-inbox-processor/enable', variant: 'secondary', body: { mode: 'single' } },
      { label: 'Enable (Regular Mode)', endpoint: '/ui/admin/dsar-inbox-processor/enable', variant: 'secondary', body: { mode: 'regular' } },
    ],
    hasStatePanel: true,
  },

  {
    id: 'automated_loc_sender',
    name: 'Automated LOC Sender',
    description: 'Automatically sends Letters of Claim to eligible clients on a regular schedule.',
    statusEndpoint: '/ui/admin/automated-loc-sender',
    enableEndpoint: '/ui/admin/automated-loc-sender/enable',
    disableEndpoint: '/ui/admin/automated-loc-sender/disable',
    runActions: [
      // Refresh Preview renders the response's scalar fields (totalEligible /
      // withValidEmails / withoutEmails / nextBatchSize / maxPerRun) via
      // GenericProcessorPanel's action-result block (see the 2026-07-14
      // "dead preview buttons" fix) — full parity, not a generic done message.
      { label: 'Refresh Preview', endpoint: '/ui/admin/automated-loc-sender/preview', method: 'GET', variant: 'secondary' },
    ],
    hasStatePanel: true,
  },

  {
    id: 'photo_id_request',
    name: 'Photo ID Request',
    description: 'Automatically sends photo ID verification requests to clients with claims that have LOCs sent.',
    // Task 3: escalated to a bespoke PhotoIdPanel (status/enable/disable/run
    // PLUS "Process Full Backlog", which starts a server-side job and polls
    // `/ui/photo-id/processor/backlog-progress/{jobId}` every 3s with a live
    // progress bar, gated behind a window.confirm) — the job-id-keyed poller
    // has no equivalent in the generic schema (single fixed statusEndpoint,
    // no per-action follow-up polling), so the whole card is bespoke rather
    // than split between a generic entry and an additive section. All 5
    // endpoints (status, enable, disable, run, run-full-backlog +
    // backlog-progress/{jobId}) are implemented in panels/PhotoIdPanel.tsx.
    hasStatePanel: true,
    component: PhotoIdPanel,
  },

  {
    id: 'unknown_names_fix',
    name: 'Unknown Names Fix',
    description: 'Fix client records that have "Unknown" as first or last name by extracting names from their claims data.',
    // Old panel polled this same status endpoint every 1s (instead of the
    // generic panel's 15s) while a fix run was in progress, for a snappier
    // live progress bar. The status shape (running/total/processed/fixed/
    // no_claims/no_name_found/errors/current_client) is scalar-only and still
    // renders correctly here — only the poll cadence during an active run is
    // degraded, not lost. Section is currently retired/hidden behind
    // `SHOW_RETIRED_PROCESSOR_SECTIONS = false` in the old file.
    statusEndpoint: '/ui/admin/fix-unknown-names/status',
    runActions: [
      { label: 'Run Fix Process', endpoint: '/ui/admin/fix-unknown-names/run', variant: 'primary' },
      { label: 'Refresh Count', endpoint: '/ui/admin/fix-unknown-names/count', method: 'GET', variant: 'secondary' },
    ],
    // Parity with `SHOW_RETIRED_PROCESSOR_SECTIONS = false` in ProcessorsManagement.tsx.
    retired: true,
  },

  {
    id: 'cancelled_claims_sync',
    name: 'Cancelled Claims Sync',
    description: 'Automatically syncs cancelled claims (after 01/09/25) to the ClaimsGate API for processing.',
    statusEndpoint: '/ui/admin/cancelled-claims-sync',
    enableEndpoint: '/ui/admin/cancelled-claims-sync/enable',
    disableEndpoint: '/ui/admin/cancelled-claims-sync/disable',
    runActions: [
      { label: 'Run Now', endpoint: '/ui/admin/cancelled-claims-sync/run', variant: 'primary' },
    ],
    // Parity with `SHOW_RETIRED_PROCESSOR_SECTIONS = false` in ProcessorsManagement.tsx.
    retired: true,
  },

  {
    id: 'client_xml_export',
    name: 'Client XML Export',
    description: 'Exports client data to XML format and stores in Azure Blob Storage.',
    statusEndpoint: '/ui/admin/client-xml-export',
    enableEndpoint: '/ui/admin/client-xml-export/enable',
    disableEndpoint: '/ui/admin/client-xml-export/disable',
    runActions: [
      { label: 'Run Now', endpoint: '/ui/admin/client-xml-export/run', variant: 'primary', body: { dryRun: false, batchSize: 50 } },
      { label: 'Test Export (1 Client)', endpoint: '/ui/admin/client-xml-export/test', variant: 'secondary' },
    ],
  },

  {
    id: 'post_document',
    name: 'Post Document',
    description: 'Processes PDF documents from Azure Blob Storage and creates tasks with automatic claim assignment.',
    statusEndpoint: '/ui/admin/processors/post-documents/status',
    enableEndpoint: '/ui/admin/processors/post-documents/enable',
    disableEndpoint: '/ui/admin/processors/post-documents/disable',
    runActions: [
      { label: 'Process Now', endpoint: '/ui/admin/processors/post-documents/run', variant: 'primary' },
      { label: 'Test Single', endpoint: '/ui/admin/processors/post-documents/test-run', variant: 'secondary' },
      { label: 'Test Single (Vision)', endpoint: '/ui/admin/processors/post-documents/test-run-vision', variant: 'secondary' },
      { label: 'Preview Pending', endpoint: '/ui/admin/processors/post-documents/preview', method: 'GET', variant: 'secondary' },
    ],
    hasStatePanel: true,
  },

  {
    id: 'call_tasks_processor',
    name: 'Call Tasks',
    description: 'Automatically processes call recordings from Google Cloud Storage, transcribes them using OpenAI Whisper, and creates review tasks.',
    statusEndpoint: '/ui/admin/call-tasks-processor',
    enableEndpoint: '/ui/admin/call-tasks-processor/enable',
    disableEndpoint: '/ui/admin/call-tasks-processor/disable',
    runActions: [
      { label: 'Run Now', endpoint: '/ui/admin/call-tasks-processor/run', variant: 'primary' },
      { label: 'Process One File (Test)', endpoint: '/ui/admin/call-tasks-processor/process-one', variant: 'secondary' },
    ],
    // Retired/hidden behind `SHOW_RETIRED_PROCESSOR_SECTIONS = false` in the old file.
    retired: true,
  },

  {
    id: 'caf_email_embedding_job',
    name: 'CAF Embedding',
    description: 'Polls email_tasks for rows without a caf_email_embeddings entry and embeds them via Azure OpenAI (text-embedding-3-small). Feeds the RAG retrieval layer used by the CAF classifier. Embedding only — never writes corpus labels.',
    statusEndpoint: '/ui/admin/processors/caf-embedding/status',
    enableEndpoint: '/ui/admin/processors/caf-embedding/enable',
    disableEndpoint: '/ui/admin/processors/caf-embedding/disable',
    runActions: [
      { label: 'Run Once', endpoint: '/ui/admin/processors/caf-embedding/run', variant: 'primary' },
    ],
    hasStatePanel: true,
  },

  {
    id: 'caf_corpus_cluster_job',
    name: 'CAF Corpus Cluster',
    description: 'Nightly job that groups caf_email_embeddings within-sender by cosine similarity and writes clusters to caf_email_clusters for staff to browse and label in the CAF Corpus Manager. Idempotent. Never writes labels — only similarity groupings.',
    statusEndpoint: '/ui/admin/processors/caf-corpus-cluster/status',
    enableEndpoint: '/ui/admin/processors/caf-corpus-cluster/enable',
    disableEndpoint: '/ui/admin/processors/caf-corpus-cluster/disable',
    runActions: [
      { label: 'Run Once', endpoint: '/ui/admin/processors/caf-corpus-cluster/run', variant: 'primary' },
    ],
    hasStatePanel: true,
  },

  {
    id: 'id_verification_ai',
    name: 'ID Verification AI',
    description: 'Automatically verifies photo IDs using OpenAI Vision. Auto-approves IDs with 95%+ confidence.',
    statusEndpoint: '/ui/admin/id-verification-ai/status',
    enableEndpoint: '/ui/admin/id-verification-ai/enable',
    disableEndpoint: '/ui/admin/id-verification-ai/disable',
    runActions: [
      { label: 'Run Now', endpoint: '/ui/admin/id-verification-ai/run', variant: 'primary' },
    ],
  },

  {
    id: 'claims_sync',
    name: 'Claims Sync',
    description: 'Synchronizes claims data from the main claims container to the claims_normalized container. Keeps normalized claims up-to-date with status changes, cancellations, and other modifications.',
    statusEndpoint: '/ui/admin/processors/claims-sync/status',
    enableEndpoint: '/ui/admin/processors/claims-sync/enable',
    disableEndpoint: '/ui/admin/processors/claims-sync/disable',
    runActions: [
      { label: 'Run Now (New Only)', endpoint: '/ui/admin/processors/claims-sync/run', variant: 'primary', body: { syncAll: false } },
      { label: 'Sync All (Update Existing)', endpoint: '/ui/admin/processors/claims-sync/run', variant: 'secondary', body: { syncAll: true } },
    ],
  },
];
